import {useMemo, useState} from "react";
import {App as AntdApp, Table, Tag, Modal, Input} from "antd";
import className from "classnames/bind";
import {Button, ExportButtons, FontAwesomeIcon, Loading, PageHeader} from "~/components";
import {useDebounce} from "~/hooks";
import {rtkErrorMessage} from "~/reduxs/api/apiSlice";
import {
	useGetPsiReportsQuery,
	useGetPsiReportQuery,
	useDeletePsiReportMutation,
} from "~/reduxs/api/pagespeedApiSlice";
import PsiResult from "../components/PsiResult";
import PsiTrendChart from "../components/PsiTrendChart";
import {buildPsiExport} from "../components/exportPsi";
import {RATING_COLOR, fmtMs, fmtCls} from "../components/ratings";
import style from "../style/PageSpeed.module.scss";

const cn = className.bind(style);

/** Màu tag điểm performance theo ngưỡng Lighthouse (dùng cho cả mobile & desktop). */
const scoreColor = (score) => {
	if (score >= 90) return RATING_COLOR.good;
	if (score >= 50) return RATING_COLOR.average;
	return RATING_COLOR.poor;
};

/** Ô điểm (mobile/desktop) — Tag màu theo ngưỡng; '—' nếu bản ghi cũ chưa có desktop. */
const ScoreTag = ({score}) => (score === null || score === undefined
	? <span style={{color: '#9ca3af'}}>—</span>
	: <Tag color={scoreColor(score)}>{score}</Tag>);

function PageSpeedHistory() {

	const {notification, modal} = AntdApp.useApp();

	const [keyword, setKeyword] = useState('');
	const search = useDebounce(keyword.trim());
	const {data: reports = [], isLoading, isFetching} = useGetPsiReportsQuery(search);
	const [deletePsiReport] = useDeletePsiReportMutation();

	const [viewId, setViewId] = useState(null);
	const {data: detail, isFetching: loadingDetail} = useGetPsiReportQuery(viewId, {skip: !viewId});

	const [trendUrl, setTrendUrl] = useState(null);

	// Gom báo cáo theo URL để (a) biết URL nào đo nhiều lần → hiện nút xu hướng,
	// (b) dựng chuỗi điểm theo thời gian tăng dần cho biểu đồ.
	const byUrl = useMemo(() => {
		const map = {};
		reports.forEach((r) => {
			(map[r.url] = map[r.url] || []).push(r);
		});
		return map;
	}, [reports]);

	const trendPoints = useMemo(() => {
		if (!trendUrl) return [];
		return [...(byUrl[trendUrl] || [])]
			.sort((a, b) => String(a.created).localeCompare(String(b.created)))
			.map((r) => ({created: r.created, mobile: r.perf_score, desktop: r.desktop_perf_score ?? 0}));
	}, [trendUrl, byUrl]);

	const handleDelete = (report) => {
		modal.confirm({
			title: 'Xóa báo cáo',
			content: `Xóa báo cáo PageSpeed cho "${report.url}"?`,
			okText: 'Xóa',
			okButtonProps: {danger: true},
			cancelText: 'Hủy',
			onOk: async () => {
				try {
					await deletePsiReport(report.id).unwrap();
					notification.success({message: 'Thành công', description: 'Đã xóa báo cáo'});
				} catch (e) {
					notification.error({message: 'Lỗi', description: rtkErrorMessage(e, 'Xóa thất bại')});
				}
			},
		});
	};

	const columns = [
		{title: 'URL', dataIndex: 'url', key: 'url', ellipsis: true},
		{
			title: 'Mobile', dataIndex: 'perf_score', key: 'perf_score', width: 90,
			sorter: (a, b) => a.perf_score - b.perf_score,
			render: (score) => <ScoreTag score={score} />,
		},
		{
			title: 'Desktop', dataIndex: 'desktop_perf_score', key: 'desktop_perf_score', width: 90,
			sorter: (a, b) => (a.desktop_perf_score || 0) - (b.desktop_perf_score || 0),
			render: (score) => <ScoreTag score={score} />,
		},
		{
			title: 'CWV (mobile)', key: 'cwv', width: 180, responsive: ['md'],
			render: (_, r) => (
				<span className={cn('cwv-cell')}>
					LCP {fmtMs(r.lab_lcp)} · CLS {fmtCls(r.lab_cls)}
				</span>
			),
		},
		{
			title: 'Dữ liệu thực', key: 'field', width: 120, responsive: ['md'],
			render: (_, r) => (
				<Tag color={r.field_available ? 'green' : 'default'}>
					{r.field_available ? 'Có CrUX' : 'Chỉ Lab'}
				</Tag>
			),
		},
		{title: 'Thời gian', dataIndex: 'created', key: 'created', width: 170, responsive: ['md']},
		{
			title: '', key: 'actions', width: 150,
			render: (_, r) => (
				<div className={cn('table-actions')}>
					{(byUrl[r.url]?.length || 0) > 1 && (
						<Button small onClick={() => setTrendUrl(r.url)} title="Xu hướng điểm theo thời gian"
							leftIcon={<FontAwesomeIcon icon="fa-light fa-chart-line" />} />
					)}
					<Button small blue onClick={() => setViewId(r.id)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-eye" />} />
					<Button small red onClick={() => handleDelete(r)}
						leftIcon={<FontAwesomeIcon icon="fa-light fa-trash" />} />
				</div>
			),
		},
	];

	if (isLoading) return <Loading />;

	return (
		<div className="container">
			<PageHeader
				icon="fa-light fa-clock-rotate-left"
				title="Lịch sử PageSpeed"
				subtitle="Các lần kiểm tra tốc độ đã thực hiện"
				actions={
					<Button to="/pagespeed" primary
						leftIcon={<FontAwesomeIcon icon="fa-light fa-gauge-high" />}>
						Kiểm tra mới
					</Button>
				}
			/>

			<Input
				allowClear
				prefix={<FontAwesomeIcon icon="fa-light fa-magnifying-glass" />}
				placeholder="Tìm theo URL…"
				value={keyword}
				onChange={(e) => setKeyword(e.target.value)}
				style={{maxWidth: 320, marginBottom: 14}}
			/>

			<Table
				rowKey="id"
				columns={columns}
				dataSource={reports}
				scroll={{x: 'max-content'}}
				loading={isFetching}
				locale={{emptyText: search ? `Không có báo cáo nào khớp “${search}”` : 'Chưa có báo cáo nào'}}
				pagination={{pageSize: 10, hideOnSinglePage: true}}
			/>

			<Modal
				open={!!viewId}
				onCancel={() => setViewId(null)}
				footer={null}
				width={900}
				title={detail ? `Báo cáo: ${detail.url}` : 'Báo cáo PageSpeed'}
			>
				{loadingDetail ? <Loading noFixed /> : detail && (
					<>
						<div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: 8}}>
							<ExportButtons {...buildPsiExport(detail)} />
						</div>
						<PsiResult result={detail} />
					</>
				)}
			</Modal>

			<Modal
				open={!!trendUrl}
				onCancel={() => setTrendUrl(null)}
				footer={null}
				width={720}
				title={<span><FontAwesomeIcon icon="fa-light fa-chart-line" /> Xu hướng điểm — {trendUrl}</span>}
			>
				<PsiTrendChart points={trendPoints} />
			</Modal>
		</div>
	);
}

export default PageSpeedHistory;
